import { forwardRef } from 'react';
import type { LabelHTMLAttributes, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { colors, getMediumRGBA } from '../../../../styles/colors';
import { fontFamily, radii, space, text } from '../../../../styles/onboardingUiTokens';
import { hexWithOpacity } from '../../../../utils/hexWithOpacity';

const labelPrimaryVisualCss = css`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${space.xs};
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.body.fontSize};
  line-height: ${text.body.lineHeight};
  font-weight: 500;
  color: ${hexWithOpacity(colors.dark, 0.85)};
`;

const Root = styled.label`
  ${labelPrimaryVisualCss}
`;

const LegendRoot = styled.legend`
  ${labelPrimaryVisualCss}
  padding: 0;
  margin: 0 0 ${space.xs};
`;

const LabelText = styled.span`
  min-width: 0;
`;

const RequiredMark = styled.span`
  color: ${colors.lightRed};
`;

const OptionalPill = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: ${text.caption.fontSize};
  line-height: ${text.caption.lineHeight};
  font-weight: 500;
  color: ${getMediumRGBA(0.9)};
  background-color: ${getMediumRGBA(0.1)};
  padding: 2px ${space.xs};
  border-radius: ${radii.sm};
`;

export type LabelProps = Omit<LabelHTMLAttributes<HTMLLabelElement>, 'required'> & {
  children?: ReactNode;
  /** When true, appends a spaced red asterisk after the label text (decorative; pair with `required` on the control for a11y). */
  required?: boolean;
  optional?: boolean;
  optionalText?: string;
};

export const Label = forwardRef<HTMLLabelElement, LabelProps>(function Label(props, ref) {
  const { children, required = false, optional = false, optionalText = 'Optional', ...rest } = props;

  if (import.meta.env.DEV && required && optional) {
    console.warn('Label: both `required` and `optional` are true; showing optional state only.');
  }

  const showOptional = optional;
  const showRequired = required && !optional;

  return (
    <Root ref={ref} {...rest}>
      <LabelText>
        {children}
        {showRequired ? (
          <>
            {' '}
            <RequiredMark aria-hidden="true">*</RequiredMark>
          </>
        ) : null}
      </LabelText>
      {showOptional ? <OptionalPill>{optionalText}</OptionalPill> : null}
    </Root>
  );
});

Label.displayName = 'Label';

export type GroupLabelLegendProps = {
  children?: ReactNode;
  required?: boolean;
  optional?: boolean;
  optionalText?: string;
};

/** `<legend>` with the same primary label look as [`Label`] (for fieldsets such as phone number groups). */
export function GroupLabelLegend(props: GroupLabelLegendProps) {
  const { children, required = false, optional = false, optionalText = 'Optional' } = props;

  if (import.meta.env.DEV && required && optional) {
    console.warn('GroupLabelLegend: both `required` and `optional` are true; showing optional state only.');
  }

  const showOptional = optional;
  const showRequired = required && !optional;

  return (
    <LegendRoot>
      <LabelText>
        {children}
        {showRequired ? (
          <>
            {' '}
            <RequiredMark aria-hidden="true">*</RequiredMark>
          </>
        ) : null}
      </LabelText>
      {showOptional ? <OptionalPill>{optionalText}</OptionalPill> : null}
    </LegendRoot>
  );
}
